import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { GenericModalService } from '../../../../services/generic-modal.service';
import { BusinessProcessService } from '../../../../services/business-process.service';
import { moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { BatchService } from '../../../../services/batch.service';
import { Utils } from '../../../../services/utils';

@Component({
  selector: 'app-batch-template-modal',
  templateUrl: './batch-template-modal.component.html',
  styleUrls: ['./batch-template-modal.component.css', '../../../../styles/modals.css']
})
export class BatchTemplateModalComponent implements OnInit, AfterViewInit {

  @Input() modalTitle: string;
  itemSaved: Subject<boolean> = new Subject();
  layoutForm: FormGroup;
  objInitialState: any;
  submitted = false;
  submitClick = false;
  errorMessage = '';
  separators = [',', '.'];
  businessProcesses$: Observable<any[]>;
  fields: any[] = [];
  selectedFields: any[] = [];
  fieldsDone = false;
  isNew: boolean;
  pleaseWait = false;

  get formData() { return this.layoutForm.controls; }

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: GenericModalService,
    private formBuilder: FormBuilder,
    private businessProcessService: BusinessProcessService,
    private batchService: BatchService
  ) { }

  ngOnInit() {
    this.pleaseWait = true;
    this.isNew = this.objInitialState.batchTemplateId === undefined;
    this.createForm();
  }

  ngAfterViewInit() {
    this.getAllBusinessProcesses();
  }

  createForm() {
    this.layoutForm = this.formBuilder.group({
      name: [ '', Validators.compose( [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern('^[^<>]+$')]) ],
      batchTemplateId: 0,
      fieldsSeparator: ['', Validators.required ],
      positiveBoolean: ['', Validators.required ],
      negativeBoolean: ['', Validators.required ],
      decimalSeparator: ',',
      groupSeparator: '.',
      dateFormats: ['', Validators.required ],
      businessProcessId: [0, Validators.required ],
      fieldColumns: [
        []
      ]
    });
  }

  onClose() {
    this.itemSaved.next(false);
    this.bsModalRef.hide();
  }

  onSave() {
    this.submitClick = true;
    this.errorMessage = '';
    const layout = this.layoutForm.getRawValue();
    layout.name = layout.name.trim();
    const array = [];
    this.selectedFields.forEach( field => {
      array.push({
        batchTemplateId: layout.batchTemplateId,
        businessProcessId: layout.businessProcessId,
        fieldColumnOrder: this.selectedFields.indexOf(field),
        fieldId: field.fieldId
      });
    });
    layout.fieldColumns = array;
    if (layout.dateFormats) {
      layout.dateFormats = layout.dateFormats.split('\n');
    }
    if (this.isNew) {
      this.createLayout(layout);
    } else {
      this.editLayout(layout);
    }
  }

  createLayout(layout: any) {
    this.errorMessage = '';
    this.batchService.createBatchTemplate(layout)
      .subscribe( newLayoutId => {
        this.submitted = true;
        this.itemSaved.next(true);
        setTimeout(() => { this.submitted = false; this.bsModalRef.hide(); }, 2000);
      }, error1 => {
        let err = error1;
        if (error1.indexOf('401') !== -1) {
          err = 'Erro: Sessão expirada.';
        }
        this.errorMessage = err;
        this.submitClick = false;
      });
  }

  editLayout(layout: any) {
    this.errorMessage = '';
    this.batchService.updateBatchTemplate(layout)
      .subscribe( () => {
        this.submitted = true;
        this.itemSaved.next(true);
        setTimeout(() => { this.submitted = false; this.bsModalRef.hide(); }, 2000);
      }, error1 => {
        let err = error1;
        if (error1.indexOf('401') !== -1) {
          err = 'Erro: Sessão expirada.';
        }
        this.errorMessage = err;
        this.submitClick = false;
      });
  }

  getAllBusinessProcesses() {
    this.errorMessage = '';
    this.businessProcesses$ = this.businessProcessService.getAllBusinessProcessesPublished();
    this.businessProcesses$.subscribe(
      result => {
        if (result.length > 0 && this.isNew) {
          this.layoutForm.patchValue(
            { businessProcessId: result[0].businessProcessId }
          );
          this.getBusinessProcessFields(result[0].businessProcessId);
        }
        if (!this.isNew) {
          this.layoutForm.patchValue(
            { businessProcessId: this.objInitialState.businessProcessId }
          );
          this.getBusinessProcessFields(this.objInitialState.businessProcessId);
          this.patchValues();
        }
        this.pleaseWait = false;
      }, error1 => this.errorMessage = error1);
  }

  getBusinessProcessFields(businessProcessId: number) {
    this.errorMessage = '';
    this.fieldsDone = false;
    this.fields = [];
    this.selectedFields = [];
    let businessProcess;
    this.businessProcesses$.subscribe(
      result => {
        businessProcess = result.find( bp => bp.businessProcessId == businessProcessId);
        this.fields = businessProcess.fields.sort( (a, b) => {
          if (a.name.toUpperCase() < b.name.toUpperCase()) { return -1; }
          if (a.name.toUpperCase() > b.name.toUpperCase()) { return 1; }
          return 0;
        });
        this.fieldsDone = true;
        if (!this.isNew && businessProcessId == this.objInitialState.businessProcessId) {
          this.organizeFieldLists();
        }
      }, error1 => this.errorMessage = error1
    );
  }

  patchValues() {
    this.layoutForm.patchValue({
      name: this.objInitialState.name,
      batchTemplateId: this.objInitialState.batchTemplateId,
      fieldsSeparator: this.objInitialState.fieldsSeparator,
      positiveBoolean: this.objInitialState.positiveBoolean,
      negativeBoolean: this.objInitialState.negativeBoolean,
      decimalSeparator: this.objInitialState.decimalSeparator,
      groupSeparator: this.objInitialState.groupSeparator,
      dateFormats: this.objInitialState.dateFormats.join('\n'),
      businessProcessId: this.objInitialState.businessProcessId
    });
  }

  organizeFieldLists() {
    this.selectedFields = [];
    this.objInitialState.fieldColumns.forEach( fieldColumn => {
      delete fieldColumn.extensionData;
    });

    const fieldsIds = this.objInitialState.fieldColumns.sort(
      (a, b) => { return a.fieldColumnOrder - b.fieldColumnOrder;
      }).map( f => f.fieldId);

    for (let i = 0; i < this.objInitialState.fieldColumns.length; i++) {
      if (fieldsIds.includes(this.objInitialState.fieldColumns[i].fieldId)) {
        this.selectedFields[i] = this.fields
          .find( f => f.fieldId === this.objInitialState.fieldColumns[i].fieldId);
      }
    }

    this.selectedFields.forEach( sf => {
      this.fields.splice(this.fields.indexOf(sf), 1);
    });
  }

  onSelectAll() {
    for (let i = 0; this.fields.length !== 0; i++) {
      transferArrayItem(this.fields, this.selectedFields, 0, this.selectedFields.length);
    }
  }

  onDeselectAll() {
    for (let i = 0; this.selectedFields.length !== 0; i++) {
      transferArrayItem(this.selectedFields, this.fields, 0, this.fields.length);
    }
  }

  drop(event) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  adjustSpaces(value, formControlName) {
    Utils.adjustSpaces(value, this.layoutForm, formControlName);
  }

}
